/** Display helpers for perpetual funding rates (see components/FundingRate). */

import {
  FUTURES_PRICE_PRIORITY,
  toFuturesServiceExchange,
  type FuturesServiceExchange,
  type ServiceMarket,
} from "@/lib/prices/exchanges";

export const FUNDING_EXCHANGE_NAMES: Record<string, string> = {
  binance_futures: "Binance",
  bybit_futures: "Bybit",
  gate_futures: "Gate",
  kucoin_futures: "KuCoin",
  hyperliquid: "Hyperliquid",
  aster_futures: "Aster",
};

/** 0.0001 -> "+0.0100%". */
export function formatFundingRate(rate: number): string {
  const pct = rate * 100;
  const digits = Math.abs(pct) >= 0.1 ? 3 : 4;
  return `${pct > 0 ? "+" : ""}${pct.toFixed(digits)}%`;
}

/** Time left until `at` (unix ms): "2h 13m", "13m", "<1m". */
export function formatFundingCountdown(at: number, now = Date.now()): string {
  const minutes = Math.floor((at - now) / 60_000);
  if (minutes < 1) return "<1m";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/**
 * Futures venues listing the token, in the order the switch shows them: the
 * user's connected exchanges first (autotrade rank), then price priority.
 */
export function fundingVenues(
  markets: ServiceMarket[],
  preferred: string[]
): FuturesServiceExchange[] {
  const listed = new Set(
    markets.filter((m) => m.market === "futures").map((m) => m.exchange)
  );
  const order: FuturesServiceExchange[] = [];
  for (const code of preferred) {
    const venue = toFuturesServiceExchange(code);
    if (venue && listed.has(venue) && !order.includes(venue)) order.push(venue);
  }
  for (const venue of FUTURES_PRICE_PRIORITY) {
    if (listed.has(venue) && !order.includes(venue)) order.push(venue);
  }
  return order;
}

/** Text colour for a funding rate: green positive, red negative, grey zero or unknown. */
export function fundingRateClass(rate: number | null | undefined): string {
  if (rate === null || rate === undefined || rate === 0) return "text-text-secondary";
  return rate > 0 ? "text-green-400" : "text-red-400";
}
